import { analyticsParams, DIVIDER } from 'constants/analytics';
import { JsonObject } from 'interfaces/JsonObject';
import { omit } from 'lodash-es';
import { AnalyticsAction } from 'services/AnalyticsService';
import { dtrumInstantAction } from 'utils/dtrum';

import { ReachGoalEvents, reachGoalEvents } from './reachGoalEvents';

type CreateAnalyticsActionPayload = {
    target?: string;
    goals?: string[];
    parameter?: {
        name: string;
        value: string;
    };
};

/**
 * @example createGoalParams('Стрим - Мессенджеры и чат-боты', 'ВТБ онлайн. Чат', 'Показ popup', 'Статусы чата', 'Ошибка подключения')
 */
export function createGoalParams(...goals: string[]): Record<string, unknown> {
    const param = goals.pop();
    const params = goals.reduceRight<Record<string, unknown> | string>(
        (acc, param) => ({ [param]: acc }),
        param,
    ) as Record<string, unknown>;
    return params;
}

/**
 * @example createAnalyticsAction('2507', 'Показ popup', 'Статусы чата', 'Ошибка подключения')
 *
 * @example createAnalyticsAction({ target: '2507', goals: ['Показ popup', 'Статусы чата', 'Ошибка подключения'] });
 *
 * @example createAnalyticsAction({ target: '1099', parameter: { name: 'UserId', value: '2421' } });
 */
export function createAnalyticsAction(target: string, ...goals: string[]): AnalyticsAction;
export function createAnalyticsAction(payload: CreateAnalyticsActionPayload): AnalyticsAction;
export function createAnalyticsAction(...args: unknown[]): AnalyticsAction {
    let action: AnalyticsAction = null;

    if (typeof args[0] === 'object') {
        const { target, goals } = args[0] as CreateAnalyticsActionPayload;

        if (target && goals?.length) {
            const goalParams = createGoalParams(...goals);
            action = { ...analyticsParams, ...goalParams, target } as AnalyticsAction;
        }
    } else {
        const [target, ...goals] = args as [target: string, ...goals: string[]];
        const goalParams = createGoalParams(...goals);
        action = { ...analyticsParams, ...goalParams, target } as AnalyticsAction;
    }

    return action;
}

export function createReachGoalActionBy<T extends keyof ReachGoalEvents>(
    target: T,
    ...args: Parameters<ReachGoalEvents[T]> | never
): AnalyticsAction | null {
    const pass = reachGoalEvents[target];
    if (typeof pass === 'function') {
        return createAnalyticsAction.call(null, target, ...pass.call(null, ...args));
    }
    return null;
}

export function getActionName(action: AnalyticsAction, maxLevel = 100): string {
    const omited = omit(action, ['stream', 'product', 'target']);
    return [action.stream, action.product, getObjectKeys(omited, maxLevel).join(DIVIDER)]
        .filter(Boolean)
        .join(DIVIDER);
}

/**
 * Возвращает ключи из 'obj' рекурсивно в виде массива. Если на одном уровне больше 1 ключа, бросает исключение.
 * Значение-строка тоже попадает в итоговый массив.
 * С помощь 'maxLevel' можно ограничить длину итогового массива.
 * @throws если ключей на уровне больше 1
 */
export function getObjectKeys(obj: JsonObject, maxLevel = 4): string[] {
    const ret: string[] = [];

    const keys = Object.keys(obj);
    const key = keys.length > 0 ? keys[0] : undefined;
    if (keys.length > 1) throw new Error('keys must be at most 1');
    if (key) {
        ret.push(key);
        const val = obj[key];
        if (typeof val === 'object' && val !== null && Object.keys(val).length > 0) {
            ret.push(...getObjectKeys(val));
        } else if (val && typeof val === 'string') {
            ret.push(val);
        }
    }

    return ret.slice(0, maxLevel < 0 ? 0 : maxLevel);
}

export const sendToKa = (action: AnalyticsAction) => {
    try {
        const dtrumStr = getActionName(action);
        dtrumInstantAction({ actionName: dtrumStr });
    } catch (err) {
        //
    }
};
